/**
 * @file Shared chrome for an inline per-app configuration panel — the embedded
 * (non-modal) form rendered under the Connections detail Settings sub-tab.
 * Wraps a title, description, body, and a Save footer so the individual config
 * dialogs only supply their form body and handlers.
 */
import { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@strivelabs/ui-core/v2";

interface InlineConfigPanelProps {
  title: string;
  description: string;
  /** The form body. */
  children: ReactNode;
  onSave: () => void;
  canSave: boolean;
  saving: boolean;
}

export function InlineConfigPanel({
  title,
  description,
  children,
  onSave,
  canSave,
  saving,
}: InlineConfigPanelProps) {
  return (
    <div className="space-y-4 rounded-xl border border-custom-border bg-custom-card-bg p-4 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold tracking-table-cell text-custom-text-primary">{title}</h3>
        <p className="mt-0.5 text-sm text-custom-text-secondary">{description}</p>
      </div>
      {children}
      <div className="flex items-center justify-end border-t border-custom-border pt-3">
        <Button onClick={onSave} disabled={!canSave || saving}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  );
}
